import { useEffect, useMemo, useRef, useState } from 'react';
import {
  Checkbox,
  Field,
  Input,
  Textarea,
  makeStyles,
  mergeClasses,
  tokens,
} from '@fluentui/react-components';
import { CheckmarkCircleFilled } from '@fluentui/react-icons';
import { useTranslation } from 'react-i18next';
import { useSaveCallContent } from '../api/useMutations';
import ResourceGallery from './ResourceGallery';
import ResourceThumbnail from './ResourceThumbnail';
import type { GalleryItem } from './ResourceGallery';
import type { CallContentDto, InstructionStep } from '../types';

const useStyles = makeStyles({
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalM,
  },
  actions: {
    display: 'flex',
    flexDirection: 'column',
  },
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(4, 140px)',
    gap: tokens.spacingHorizontalS,
    marginTop: tokens.spacingVerticalXS,
  },
  clickable: {
    cursor: 'pointer',
    padding: 0,
    border: 'none',
    backgroundColor: 'transparent',
    textAlign: 'start' as const,
  },
  imageWrapper: {
    position: 'relative',
  },
  selectedBadge: {
    position: 'absolute',
    top: '4px',
    left: '4px',
    zIndex: 1,
    color: tokens.colorBrandForeground1,
    fontSize: '20px',
    filter: `drop-shadow(0 0 2px ${tokens.colorNeutralBackground1})`,
  },
  unselectedImage: {
    opacity: 0.4,
  },
});

interface StepTabProps {
  step: InstructionStep;
  stepImageIds: string[];
  allImageIds: string[];
  comment?: string;
  completedActionIds: number[];
  domainId: number;
  isEditing?: boolean;
  saveSignal?: number;
  resetSignal?: number;
  callId?: number;
  contentId?: string;
  content?: CallContentDto;
}

export default function StepTab({
  step,
  stepImageIds,
  allImageIds,
  comment,
  completedActionIds,
  domainId,
  isEditing = false,
  saveSignal = 0,
  resetSignal = 0,
  callId = 0,
  contentId,
  content,
}: StepTabProps) {
  const styles = useStyles();
  const { t } = useTranslation();
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);

  // Editable state
  const [editCompletedIds, setEditCompletedIds] = useState<number[]>(completedActionIds);
  const [editComment, setEditComment] = useState(comment ?? '');
  const [editSelectedImageIds, setEditSelectedImageIds] = useState<string[]>(stepImageIds);

  // Reset on cancel
  const prevResetSignal = useRef(resetSignal);
  useEffect(() => {
    if (resetSignal !== prevResetSignal.current) {
      prevResetSignal.current = resetSignal;
      setEditCompletedIds(completedActionIds);
      setEditComment(comment ?? '');
      setEditSelectedImageIds(stepImageIds);
    }
  }, [resetSignal, completedActionIds, comment, stepImageIds]);

  // Reset when entering edit mode
  useEffect(() => {
    if (isEditing) {
      setEditCompletedIds(completedActionIds);
      setEditComment(comment ?? '');
      setEditSelectedImageIds(stepImageIds);
    }
  }, [isEditing, completedActionIds, comment, stepImageIds]);

  // Mutation
  const saveContent = useSaveCallContent(callId, contentId);

  const saveRef = useRef(() => {});
  saveRef.current = () => {
    const actionsChanged = JSON.stringify([...editCompletedIds].sort()) !== JSON.stringify([...completedActionIds].sort());
    const commentChanged = editComment !== (comment ?? '');
    const imagesChanged = JSON.stringify([...editSelectedImageIds].sort()) !== JSON.stringify([...stepImageIds].sort());

    if (!actionsChanged && !commentChanged && !imagesChanged) return;

    const updatedComments = [...(content?.comments ?? [])];
    if (commentChanged) {
      const existingIdx = updatedComments.findIndex(c => c.stepId === step.id);
      if (existingIdx >= 0) {
        updatedComments[existingIdx] = { ...updatedComments[existingIdx], comment: editComment };
      } else if (editComment) {
        updatedComments.push({ id: 0, stepId: step.id, comment: editComment, addedAt: new Date().toISOString() });
      }
    }

    let updatedResources = content?.resources ?? [];
    if (imagesChanged) {
      updatedResources = updatedResources.map(r => {
        const fileId = r.uri.match(/^(?:\w+:\/\/)([^/?]+)/)?.[1];
        if (r.type !== 'image' || !fileId) return r;

        const wasOnStep = stepImageIds.includes(fileId);
        const isNowOnStep = editSelectedImageIds.includes(fileId);

        if (wasOnStep && !isNowOnStep) {
          return { ...r, stepIds: r.stepIds.filter(sid => String(sid) !== String(step.id)) };
        }
        if (!wasOnStep && isNowOnStep) {
          return { ...r, stepIds: [...r.stepIds, step.id] };
        }
        return r;
      });
    }

    saveContent.mutate({
      ...(content ?? {}),
      completedActionIds: content?.instructionSetId
        ? (actionsChanged ? editCompletedIds : (content?.completedActionIds ?? []))
        : null,
      comments: commentChanged ? updatedComments : (content?.comments ?? []),
      resources: imagesChanged ? updatedResources : (content?.resources ?? []),
    });
  };

  const prevSaveSignal = useRef(saveSignal);
  useEffect(() => {
    if (saveSignal !== prevSaveSignal.current) {
      prevSaveSignal.current = saveSignal;
      saveRef.current();
    }
  }, [saveSignal]);

  const handleToggleAction = (actionId: number) => {
    setEditCompletedIds(prev =>
      prev.includes(actionId) ? prev.filter(id => id !== actionId) : [...prev, actionId],
    );
  };

  const handleToggleImage = (imageId: string) => {
    setEditSelectedImageIds(prev =>
      prev.includes(imageId) ? prev.filter(id => id !== imageId) : [...prev, imageId],
    );
  };

  const displayedImageIds = isEditing ? allImageIds : editSelectedImageIds;

  const galleryItems: GalleryItem[] = useMemo(
    () => displayedImageIds.map(id => ({ fileId: id, type: 'image' as const })),
    [displayedImageIds],
  );

  const activeCompletedIds = isEditing ? editCompletedIds : completedActionIds;
  const activeComment = isEditing ? editComment : (comment ?? '');

  return (
    <div className={styles.container}>
      <Field label={t('headline')}>
        <Input value={step.mainText || step.text} readOnly appearance={isEditing ? 'filled-darker' : undefined} />
      </Field>

      {step.secondaryText && (
        <Field label={t('detailedInstruction')}>
          <Textarea value={step.secondaryText} readOnly appearance={isEditing ? 'filled-darker' : undefined} resize="vertical" rows={2} />
        </Field>
      )}

      {(activeComment || isEditing) && (
        <Field label={t('notes')}>
          <Textarea
            value={activeComment}
            onChange={isEditing ? (_, data) => setEditComment(data.value) : undefined}
            readOnly={!isEditing}
            appearance={!isEditing ? 'filled-darker' : undefined}
            resize="vertical"
            rows={2}
          />
        </Field>
      )}

      {step.actions && step.actions.length > 0 && (
        <div className={styles.actions}>
          {step.actions.map(action => (
            <Checkbox
              key={action.id}
              label={action.text}
              checked={activeCompletedIds.includes(action.id)}
              disabled={!isEditing}
              onChange={isEditing ? () => handleToggleAction(action.id) : undefined}
            />
          ))}
        </div>
      )}

      {displayedImageIds.length > 0 && (
        <div className={styles.grid}>
          {displayedImageIds.map((id, index) => {
            const isSelected = editSelectedImageIds.includes(id);
            return (
              <button
                key={id}
                className={mergeClasses(styles.imageWrapper, styles.clickable, isEditing && !isSelected && styles.unselectedImage)}
                onClick={() => isEditing ? handleToggleImage(id) : setSelectedIndex(index)}
                aria-label={isEditing ? `${t('images')} ${index + 1}` : `${t('images')} ${index + 1}`}
                aria-pressed={isEditing ? isSelected : undefined}
              >
                {isEditing && isSelected && <CheckmarkCircleFilled className={styles.selectedBadge} />}
                <ResourceThumbnail domainId={domainId} fileId={id} />
              </button>
            );
          })}
        </div>
      )}

      {selectedIndex !== null && (
        <ResourceGallery
          items={galleryItems}
          startIndex={selectedIndex}
          domainId={domainId}
          onClose={() => setSelectedIndex(null)}
        />
      )}
    </div>
  );
}
